import React, { useCallback, useEffect, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';

import { useDispatch } from 'react-redux';

import { CustomChipsAutocomplete } from 'components/shared/DropDown/CustomChipsAutocomplete';
import { CustomAutocompleteMultipleChoice } from 'components/shared/DropDown/CustomAutocompleteMultipleChoice';

import { PWD_REGEX, EMAIL_REGEX, PHONENUMBER_REGEX } from 'utils/Regex';
import { LANGUAGES, ROLES } from 'utils/Constants';
import { lower } from 'utils/StringUtils';

import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { Modal } from 'reactstrap';

import { classes } from 'components/shared/modal/modalClasses';
import { disabledInput } from 'components/shared/disabledClasses';
import { UpDownInputArrows } from 'components/shared/UpDownInputArrows';

import { MAIN_LANGUAGES } from 'utils/Constants';
import { useTranslation } from 'react-i18next';
import { UserLanguageSelector } from '../UserLanguageSelector';
import { LanguageSelector } from '../LanguageSelector';
import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import { addUser, updateUser } from 'store/AdminClient/CLientAdminManagement';
import { useSelector } from 'react-redux';
import { UserSessionSelector } from 'store/UserSession';
import './UmModal.css';

const API = {
  createCLientAdmin: '/user/external',
  updateCLientAdmin: '/user/external',
  getAvailableThematics: '/user/thematic/all',
  getAvailableLanguages: '/user/language/all',
  getAllWebsiteOfAccount: 'project/website/all',
};

export const AdminClientModal = (props) => {
  const { user: currentUser } = useSelector(UserSessionSelector);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [mail, setMail] = useState('');
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState('');
  const [minRate, setMinRate] = useState(0);
  const [maxRate, setMaxRate] = useState(0);
  const [websiteList, setWebsiteList] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const [limit, setLimit] = useState(0);

  const [address, setAddress] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [availableThematicsList, setAvailableThematicsList] = useState([]);
  const [availableLanguagesList, setAvailableLanguagesList] = useState([]);
  const [account, setAccount] = useState(null);
  const [mainWriterLanguages, setMainWriterLanguages] = useState([]);
  const [minorWriterLanguages, setMinorWriterLanguages] = useState([]);
  const [writerThematics, setWriterThematics] = useState([]);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const [disabledForm, setDisabledForm] = useState(true);

  useEffect(() => {
    // quand on ouvre en création, on copie la société du parent
    if (!props.user) {
      setCompanyName(currentUser?.companyName || '');
    } else {
       setCompanyName(props.user?.companyName || currentUser?.companyName || '');
    }
  }, [props.user, currentUser]);
  useEffect(() => {
    axiosPrivate
      .get(API.getAvailableLanguages)
      .then((res) => setAvailableLanguagesList(res.data));

    axiosPrivate
      .get(API.getAvailableThematics)
      .then((res) => setAvailableThematicsList(res.data));
  }, []);

  useEffect(() => {
    if (!account) {
      setWebsiteList([]);
      return;
    }

    axiosPrivate
      .get(API.getAllWebsiteOfAccount, { params: { account: account } })
      .then((res) => setWebsiteList(res.data));
  }, [account]);

  useEffect(() => {
    if (props && props.user) {
      setMail(props.user.mail);
      setName(props.user.firstName);
      setLastName(props.user.lastName);
      setPhoneNumber(props.user.phoneNumber);

      const lang = LANGUAGES.find(
        (l) => l.isoCode === props.user.isoLanguageCode
      );
      setSelectedLanguage(lang || null);
    }
  }, [props.user]);

  const checkPasswordValid = useCallback(() => {
    return !password || PWD_REGEX.test(password);
  }, [password]);

  const checkValidForm = useCallback(() => {
    if (!mail || !name || !lastName || !selectedLanguage) {
      setDisabledForm(true);
      return;
    }

    if (mail.length > 0 && !mail.match(EMAIL_REGEX)) {
      setDisabledForm(true);
      return;
    }

    return setDisabledForm(!checkPasswordValid());
  }, [
    address,
    checkPasswordValid,
    companyName,
    mail,
    mainWriterLanguages,
    minorWriterLanguages,
    name,
    phoneNumber,
    writerThematics,
    selectedLanguage,
  ]);

  useEffect(() => {
    checkValidForm();
  }, [
    mail,
    name,
    password,
    phoneNumber,
    companyName,
    address,
    writerThematics,
    mainWriterLanguages,
    minorWriterLanguages,
    selectedLanguage,
    checkValidForm,
  ]);

  const callbackFromThematicMultiSelect = (newList) => {
    setWriterThematics(newList);
  };

  const callbackFromLanguagesMultiSelect = (newList) => {
    setMinorWriterLanguages(newList);
  };

  const findLanguageIndex = (language) => {
    return mainWriterLanguages.findIndex((element) =>
      lower(element.language).includes(lower(language))
    );
  };

  const checkLanguage = (language) => {
    let index = findLanguageIndex(language);
    let array = [...mainWriterLanguages];
    if (index > -1) {
      array.splice(index, 1);
    } else {
      let item = availableLanguagesList.find((element) =>
        lower(element.language).includes(lower(language))
      );
      array.push(item);
    }

    setMainWriterLanguages(array);
  };

  const updateCLientAdmin = () => {
    const inheritedCompanyName = companyName?.trim() || currentUser?.companyName || '';
    let body = {
      password: !password ? null : password,
      mail,
      firstName: name,
      lastName: lastName,
      phoneNumber,
      role: ROLES.ADMIN_CLIENT,
      isoLanguageCode: selectedLanguage.isoCode,
      companyName: inheritedCompanyName,
    };

    axiosPrivate
      .put(API.updateCLientAdmin, body, {
        params: { mail: props.user.mail },
      })
      .then((res) => {
        dispatch(updateUser({ ...res.data, _originalMail: props.user.mail }));
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le admin client')} ${props.user.mail} ${t(
            'a été mis à jour avec succès'
          )} !`
        );

        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };

  const createCLientAdmin = () => {
    const inheritedCompanyName = companyName?.trim() || currentUser?.companyName || '';
    let body = {
      mail: mail,
      firstName: name,
      lastName: lastName,
      phoneNumber: phoneNumber,
      role: ROLES.ADMIN_CLIENT,
      isoLanguageCode: selectedLanguage.isoCode,
      companyName: inheritedCompanyName,
    };

    axiosPrivate
      .post(API.createCLientAdmin, body)
      .then((res) => {
        dispatch(addUser({ ...res.data, role: ROLES.ADMIN_CLIENT }));
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le admin client')} ${mail} ${t('a été créé avec succès')}`
        );

        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };

  if (props.user)
    return (
      <div>
        <Modal isOpen={props.open} centered contentClassName="um-modal-content">
          {/* Header */}
          <div className="um-header">
            <h5 className="um-title">{t("Editer le profil")}</h5>
            <button className="um-btn-close" onClick={props.handleClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="um-body">
            {/* Row 1 : Prénom + Nom */}
            <div className="um-row">
              <div className="um-field">
                <span className="um-label">{t('Prénom')}*</span>
                <input
                  type="text"
                  className="um-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="um-field">
                <span className="um-label">{t('Nom')}*</span>
                <input
                  type="text"
                  className="um-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            {/* Row 2 : Email */}
            <div className="um-row-full">
              <div className="um-field">
                <span className="um-label">{t('E-mail')}*</span>
                <input
                  type="text"
                  className="um-input"
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                />
              </div>
            </div>

            {/* Row 3 : Téléphone */}
            <div className="um-row-full">
              <div className="um-field">
                <span className="um-label">{t('Téléphone')}</span>
                <input
                  type="text"
                  className="um-input"
                  value={phoneNumber}
                  onChange={(e) => {
                    if (e.target.value === '' || PHONENUMBER_REGEX.test(e.target.value)) {
                      setPhoneNumber(e.target.value);
                    }
                  }}
                />
              </div>
            </div>

            {/* Row 4 : Langue + Mot de passe */}
            <div className="um-row">
              <div className="um-field">
                <LanguageSelector
                  className="um-label"
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={(option) => setSelectedLanguage(option)}
                />
              </div>
              <div className="um-field">
                <span className="um-label">{t('Mot de passe')}*</span>
                <div className="um-input-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="um-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="um-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img
                      src={showPassword ? '/Images/icon-eye-black.png' : '/Images/icon-eye-gray.png'}
                      alt="toggle password"
                    />
                  </button>
                </div>
                <p className="um-hint">
                  * {t('Avoir au moins 8 caractères avec une lettre majuscule, un chiffre, un symbole et ne pas être identique au nom.')}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="um-footer">
            <button className="um-btn-cancel" onClick={props.handleClose}>
              {t('Annuler')}
            </button>
            <button
              className="um-btn-save"
              disabled={disabledForm}
              onClick={updateCLientAdmin}
            >
              {t('Sauvegarder les modifications')}
            </button>
          </div>
        </Modal>

        <ErrorSuccesModal
          open={errorSuccess}
          message={errorSuccessMsg}
          handleClose={() => {
            if (isSuccess === true) props.handleClose();
            setErrorSuccess(false);
          }}
          success={isSuccess}
          toggle={() => setErrorSuccess(!errorSuccess)}
        />
      </div>
    );

  // ─── CREATE MODAL ─────────────────────────────────────────────────────────────
  return (
    <div>
      <Modal isOpen={props.open} centered contentClassName="um-modal-content">
        {/* Header */}
        <div className="um-header">
          <h5 className="um-title">{t('Ajouter un admin')}</h5>
          <button className="um-btn-close" onClick={props.handleClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="um-body">
          {/* Row 1 : Prénom + Nom */}
          <div className="um-row">
            <div className="um-field">
              <span className="um-label">{t('Prénom')}*</span>
              <input
                type="text"
                className="um-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="um-field">
              <span className="um-label">{t('Nom')}*</span>
              <input
                type="text"
                className="um-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          {/* Row 2 : Email */}
          <div className="um-row-full">
            <div className="um-field">
              <span className="um-label">{t('E-mail')}*</span>
              <input
                type="text"
                className="um-input"
                value={mail}
                onChange={(e) => setMail(e.target.value)}
              />
            </div>
          </div>

          {/* Row 3 : Téléphone */}
          <div className="um-row-full">
            <div className="um-field">
              <span className="um-label">{t('Téléphone')}</span>
              <input
                type="text"
                className="um-input"
                value={phoneNumber}
                onChange={(e) => {
                  if (e.target.value === '' || PHONENUMBER_REGEX.test(e.target.value)) {
                    setPhoneNumber(e.target.value);
                  }
                }}
              />
            </div>
          </div>

          {/* Row 4 : Langue + Société */}
          <div className="um-row">
            <div className="um-field">
              <LanguageSelector
                className="um-label"
                selectedLanguage={selectedLanguage}
                onLanguageChange={(option) => setSelectedLanguage(option)}
              />
            </div>
            <div className="um-field">
              <span className="um-label">{t('Société')}</span>
              <input
                type="text"
                className="um-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={true}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="um-footer">
          <button className="um-btn-cancel" onClick={props.handleClose}>
            {t('Annuler')}
          </button>
          <button
            className="um-btn-save"
            disabled={disabledForm}
            onClick={createCLientAdmin}
          >
            {t('Sauvegarder')}
          </button>
        </div>
      </Modal>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          if (isSuccess === true) props.handleClose();
          setErrorSuccess(false);
        }}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
    </div>
  );
};

export default AdminClientModal;